import type { StrategyTradeNotification, TradeStatusUpdate, TradeType } from '@/types/api/quickTrade';

export const SUCCESSFUL_STRATEGY_TRADE_NOTIFICATION_EVENT = 'sobe:strategy-trade-notification:success';

export type SuccessfulTradeNotification = StrategyTradeNotification;

type SuccessfulStrategyTradeNotificationEvent = CustomEvent<SuccessfulTradeNotification>;

interface SuccessfulTradeStatusContext {
  tradeId: number;
  tradeType: TradeType;
  tokenAddress: string;
  chainId: number;
  strategyId?: number;
  strategyName?: string;
}

export function publishSuccessfulStrategyTradeNotification(
  notification: SuccessfulTradeNotification
): void {
  if (notification.status !== 'success' || typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(SUCCESSFUL_STRATEGY_TRADE_NOTIFICATION_EVENT, { detail: notification })
  );
}

export function publishSuccessfulTradeStatusUpdate(
  update: TradeStatusUpdate,
  context: SuccessfulTradeStatusContext
): void {
  if (update.status !== 'success') return;

  publishSuccessfulStrategyTradeNotification({
    trade_id: update.trade_id ?? context.tradeId,
    trade_type: update.trade_type ?? context.tradeType,
    token_address: update.token_address ?? context.tokenAddress,
    chain_id: update.chain_id ?? context.chainId,
    strategy_id: update.alpha_strategy_id ?? context.strategyId,
    strategy_name: context.strategyName,
    avg_entry_price_usd: update.avg_entry_price_usd,
    status: update.status,
    tx_hash: update.tx_hash,
    message: update.message,
    wallet_address: update.wallet_address,
  });
}

export function subscribeToSuccessfulStrategyTradeNotifications(
  onNotification: (notification: SuccessfulTradeNotification) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const listener = (event: Event) => {
    const notification = (event as SuccessfulStrategyTradeNotificationEvent).detail;
    if (!notification || notification.status !== 'success') return;

    onNotification(notification);
  };

  window.addEventListener(SUCCESSFUL_STRATEGY_TRADE_NOTIFICATION_EVENT, listener);

  return () => {
    window.removeEventListener(SUCCESSFUL_STRATEGY_TRADE_NOTIFICATION_EVENT, listener);
  };
}
